import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DatePanelItems, datePanelItems } from '../../types/resource';
import { IDashboardFilterOptions } from '../components/dashboard/types/dashboard-types';

@Injectable({
  providedIn: 'root'
})
export class FilterStateService {
  private readonly storageKey = 'shared_filter_state';

  private filterStateSubject = new BehaviorSubject<IDashboardFilterOptions>(this.getDefaultFilterOptions());
  public filterState$: Observable<IDashboardFilterOptions> = this.filterStateSubject.asObservable();

  constructor() {
    this.loadFilterState();
  }

  private getDefaultFilterOptions(): IDashboardFilterOptions {
    return {
      showCompleted: false,
      showRejected: false,
      filterOptions: datePanelItems.map((item) => {
        // Set default selections: Due = Today (key 0), Scope = My Subtasks (key 0)
        let defaultIndex = 0;
        if (item.name === 'Due') {
          defaultIndex = 0; // Today
        } else if (item.name === 'Scope') {
          defaultIndex = 0; // My Subtasks
        }

        return {
          ...item,
          selectedItem: item.items[defaultIndex].text,
        };
      })
    };
  }

  public loadFilterState(): IDashboardFilterOptions {
    const savedState = localStorage.getItem(this.storageKey);
    let filterOptions: IDashboardFilterOptions;

    if (savedState) {
      try {
        const savedOptions = JSON.parse(savedState);
        // Update dates while preserving selections
        filterOptions = {
          showCompleted: savedOptions.showCompleted || false,
          showRejected: savedOptions.showRejected || false,
          filterOptions: savedOptions.filterOptions.map(savedOption => {
            const defaultOption = datePanelItems.find(item => item.name === savedOption.name);
            if (defaultOption) {
              return {
                ...savedOption,
                items: defaultOption.items // Use fresh dates
              };
            }
            return savedOption;
          })
        };
      } catch (error) {
        filterOptions = this.getDefaultFilterOptions();
      }
    } else {
      filterOptions = this.getDefaultFilterOptions();
    }

    this.filterStateSubject.next(filterOptions);
    return filterOptions;
  }

  public saveFilterState(filterOptions: IDashboardFilterOptions): void {
    localStorage.setItem(this.storageKey, JSON.stringify(filterOptions));
    this.filterStateSubject.next(filterOptions);
  }

  public updateFilterState(updates: Partial<IDashboardFilterOptions>): void {
    const currentState = this.filterStateSubject.value;
    const newState = { ...currentState, ...updates };
    this.saveFilterState(newState);
  }

  public getCurrentFilterState(): IDashboardFilterOptions {
    return this.filterStateSubject.value;
  }

  // Helper methods for specific filter types
  public updateShowCompleted(showCompleted: boolean): void {
    this.updateFilterState({ showCompleted });
  }

  public updateShowRejected(showRejected: boolean): void {
    this.updateFilterState({ showRejected });
  }

  public updateFilterOptions(filterOptions: DatePanelItems[]): void {
    this.updateFilterState({ filterOptions });
  }

  public clearFilterState(): void {
    // Remove the stored filter state from localStorage
    localStorage.removeItem(this.storageKey);

    // Reset to default filter options
    const defaultState = this.getDefaultFilterOptions();
    this.filterStateSubject.next(defaultState);
  }
}
